'use client'

import * as React from 'react'
import { cn } from '@/lib/utils'
import { pillBtnBase } from './pill-button'

// Full-width segmented control — ui.jsx:122-149.
export interface SegOption<T extends string = string> {
  value: T
  label: React.ReactNode
  /** Leading icon node (size ~17 to match the prototype). */
  icon?: React.ReactNode
  count?: number
  /** Extra classes for the SELECTED state (merged after the defaults, so
   * e.g. a primary-container tint can override the plain surface). */
  activeClassName?: string
}

export interface SegProps<T extends string = string> {
  options: SegOption<T>[]
  value: T
  onChange: (value: T) => void
  /** Smaller paddings/type for tight surfaces (e.g. inside cards). */
  compact?: boolean
  className?: string
  style?: React.CSSProperties
}

export function Seg<T extends string = string>({ options, value, onChange, compact, className, style }: SegProps<T>) {
  return (
    <div
      className={cn('flex gap-[2px] rounded-full bg-surface-container', compact ? 'p-[3px]' : 'p-1', className)}
      style={style}
    >
      {options.map((o) => {
        const on = o.value === value
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            title={typeof o.label === 'string' ? o.label : undefined}
            className={cn(
              pillBtnBase,
              // basis AUTO, not 0: `flex-1` (basis 0) divides the row into
              // equal parts regardless of content, so the widest label
              // truncated even with room to spare ("Map" → "M…" in the
              // search toolbar, needing ~79px of an even 78px split).
              // `flex-[1_1_auto]` starts from each label's own width and
              // shares only the LEFTOVER space, so nothing truncates while
              // the control fits. min-w-0 still allows shrinking past the
              // content when it genuinely doesn't: a flex item defaults to
              // `min-width:auto`, so with `whitespace-nowrap` (pillBtnBase) a
              // 4-segment Seg pushed past its container and scrolled the whole
              // PAGE sideways on a 375px phone (measured on /settings).
              'min-w-0 flex-[1_1_auto] justify-center [&>svg]:shrink-0',
              compact ? 'px-[10px] py-[5px] text-[11.5px]' : 'px-[14px] py-2 text-[13px]',
              on ? 'bg-surface text-ink shadow-e1' : 'bg-transparent text-ink-secondary shadow-none',
              on && o.activeClassName
            )}
          >
            {o.icon}
            <span className="min-w-0 truncate">{o.label}</span>
            {o.count != null && o.count > 0 ? (
              <span
                className={cn(
                  'rounded-full px-[7px] py-px font-mono text-[11px] font-bold',
                  on ? 'bg-primary-container text-on-primary-container' : 'bg-surface-high text-ink-muted'
                )}
              >
                {o.count}
              </span>
            ) : null}
          </button>
        )
      })}
    </div>
  )
}
